import React, { useState, useEffect, useCallback } from "react";
import { Layers } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import TemplatePreviewCard from "./TemplatePreviewCard";
import ChangeTemplateDialog from "./ChangeTemplateDialog";

/**
 * TenantTemplateSection — embedded di PlatformTenantDetail.
 * Menampilkan template aktif tenant + tombol Change Template.
 *
 * Props: tenantId
 */
export default function TenantTemplateSection({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChange, setShowChange] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await api.get(`/platform/tenants/${tenantId}/template`);
      setData(r.data);
    } catch (e) { notifyError(e, "Gagal memuat template tenant"); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const isRegistryBased = data?.template_source === "TEMPLATE_REGISTRY";

  return (
    <Card className="bg-white/5 border-white/10 p-5 rounded-2xl" data-testid="tenant-template-section">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-display font-bold text-white flex items-center gap-2">
          <Layers size={14} /> Template
        </h3>
        {!loading && data && !isRegistryBased && (
          <>
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-300/80 border border-amber-500/30 bg-amber-500/10 rounded-full px-2.5 py-1" data-testid="tenant-template-legacy-badge">
              Legacy
            </span>
            <Button size="sm" onClick={() => setShowChange(true)} className="ml-auto bg-white text-black hover:bg-white/90 h-8"
              data-testid="btn-change-template">
              Ganti Template
            </Button>
          </>
        )}
        {isRegistryBased && (
          <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-cyan-300 border border-cyan-500/30 bg-cyan-500/10 rounded-full px-2.5 py-1" data-testid="tenant-template-registry-badge">
            Template Registry
          </span>
        )}
      </div>

      {loading || !data ? (
        <div className="text-white/40 text-sm p-4">Memuat template…</div>
      ) : isRegistryBased ? (
        <div className="text-sm text-white/70 space-y-2" data-testid="tenant-template-registry-info">
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-white/40 block mb-0.5">Template Source</span>
            <div className="text-white font-display font-black text-lg" data-testid="tenant-template-name">{data.source_template_name}</div>
            <div className="text-[11px] text-white/50 font-mono">{data.source_template_slug} · Template Registry snapshot</div>
          </div>
          <p className="text-[12px] text-white/50">
            Tenant ini memakai snapshot design/DNA/demo dari Template Registry saat dibuat.
            Legacy template tidak berlaku dan tidak dapat menimpa tenant ini.
          </p>
        </div>
      ) : !data.template ? (
        <div className="text-rose-400 text-sm p-4">
          Template <code className="font-mono">{data.template_key}</code> tidak ditemukan di registry.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[220px_1fr]">
          <div>
            <TemplatePreviewCard template={data.template} />
          </div>
          <div className="text-sm text-white/70 space-y-2">
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-white/40 block mb-0.5">Current Template</span>
              <div className="text-white font-display font-black text-lg" data-testid="tenant-template-name">{data.template.template_name}</div>
              <div className="text-[11px] text-white/50 font-mono">{data.template.template_key} · v{data.template.version}</div>
            </div>
            {data.template.description && <div className="text-[13px] text-white/60">{data.template.description}</div>}
            <div className="pt-2">
              <div className="text-[10px] font-black uppercase tracking-wider text-white/40 mb-1">Metadata</div>
              <div className="grid grid-cols-2 gap-1 text-[11px] font-mono">
                {Object.entries(data.template.metadata || {}).map(([k, v]) => (
                  <div key={k} className="text-white/50">
                    <span className="text-white/30">{k}:</span> <span className="text-white/70">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isRegistryBased && (
        <ChangeTemplateDialog
          tenantId={tenantId}
          currentKey={data?.template_key}
          open={showChange}
          onClose={() => setShowChange(false)}
          onChanged={() => load()}
        />
      )}
    </Card>
  );
}
